import type { CreateExpenseInput, CreateIncomeInput, FinanceRepository, TenantContext } from '@zetyar/data-contracts'
import type { ExpenseRecord, FinanceCategory, IncomeRecord, UUID } from '@zetyar/domain'
import type { SupabaseClient } from '@supabase/supabase-js'

export function createSupabaseFinanceRepository(client: SupabaseClient): FinanceRepository {
  return {
    async listOverview(context: TenantContext, { limit }: { readonly limit: number }) {
      const boundedLimit = Math.max(1, Math.min(limit, 200))

      const [categoriesRes, incomeRes, expensesRes] = await Promise.all([
        client
          .from('finance_categories')
          .select('id, organization_id, name, color, is_active, sort_order, created_at')
          .eq('organization_id', context.organizationId)
          .eq('is_active', true)
          .order('sort_order', { ascending: true }),
        client
          .from('income_records')
          .select('id, organization_id, category_id, amount, currency, description, source, date, created_by_membership_id, created_at')
          .eq('organization_id', context.organizationId)
          .is('deleted_at', null)
          .order('date', { ascending: false })
          .limit(boundedLimit),
        client
          .from('expense_records')
          .select('id, organization_id, category_id, amount, currency, description, vendor, date, status, approved_by_membership_id, approved_at, created_by_membership_id, created_at')
          .eq('organization_id', context.organizationId)
          .is('deleted_at', null)
          .order('date', { ascending: false })
          .limit(boundedLimit),
      ])

      if (categoriesRes.error) throw categoriesRes.error
      if (incomeRes.error) throw incomeRes.error
      if (expensesRes.error) throw expensesRes.error

      const categories: FinanceCategory[] = categoriesRes.data.map((item) => ({
        id: item.id as UUID,
        organizationId: item.organization_id as UUID,
        name: item.name as string,
        description: null,
        color: (item.color as string | null) ?? '#0E6B5C',
        isActive: item.is_active as boolean,
        sortOrder: item.sort_order as number,
        createdAt: item.created_at as string,
      }))

      const income: IncomeRecord[] = incomeRes.data.map((item) => ({
        id: item.id as UUID,
        organizationId: item.organization_id as UUID,
        categoryId: (item.category_id as UUID | null) ?? '',
        amount: Number(item.amount),
        currency: item.currency as string,
        description: item.description as string,
        referenceNumber: null,
        receiptUrl: null,
        source: (item.source as string | null) ?? null,
        date: item.date as string,
        createdBy: item.created_by_membership_id as UUID,
        createdAt: item.created_at as string,
        updatedAt: item.created_at as string,
        deletedAt: null,
      }))

      const expenses: ExpenseRecord[] = expensesRes.data.map((item) => ({
        id: item.id as UUID,
        organizationId: item.organization_id as UUID,
        categoryId: (item.category_id as UUID | null) ?? '',
        amount: Number(item.amount),
        currency: item.currency as string,
        description: item.description as string,
        referenceNumber: null,
        receiptUrl: null,
        vendor: (item.vendor as string | null) ?? null,
        date: item.date as string,
        status: item.status as ExpenseRecord['status'],
        approvedBy: (item.approved_by_membership_id as UUID | null) ?? null,
        approvedAt: (item.approved_at as string | null) ?? null,
        createdBy: item.created_by_membership_id as UUID,
        createdAt: item.created_at as string,
        updatedAt: item.created_at as string,
        deletedAt: null,
      }))

      return { categories, income, expenses }
    },

    async createIncome(context: TenantContext, input: CreateIncomeInput) {
      const { error } = await client.rpc('create_income', {
        p_organization_id: context.organizationId,
        p_amount: Math.round(input.amount),
        p_description: input.description,
        p_date: input.date,
        p_category_id: input.categoryId ?? null,
        p_currency: input.currency ?? 'CLP',
        p_source: input.source ?? null,
      })
      if (error) throw error
    },

    async deleteIncome(context: TenantContext, incomeId: UUID) {
      const { error } = await client.rpc('delete_income', { p_id: incomeId, p_organization_id: context.organizationId })
      if (error) throw error
    },

    async createExpense(context: TenantContext, input: CreateExpenseInput) {
      const { error } = await client.rpc('create_expense', {
        p_organization_id: context.organizationId,
        p_amount: Math.round(input.amount),
        p_description: input.description,
        p_date: input.date,
        p_category_id: input.categoryId ?? null,
        p_currency: input.currency ?? 'CLP',
        p_vendor: input.vendor ?? null,
      })
      if (error) throw error
    },

    async decideExpense(context: TenantContext, expenseId: UUID, createdByMembershipId: UUID, decision: 'approved' | 'rejected') {
      if (!context.membershipId) throw new Error('Membership is required to decide an expense')

      const { error } = await client.rpc('decide_expense', {
        p_id: expenseId,
        p_organization_id: context.organizationId,
        p_notify_membership_id: createdByMembershipId,
        p_status: decision,
      })
      if (error) throw error
    },

    async deleteExpense(context: TenantContext, expenseId: UUID) {
      const { error } = await client.rpc('delete_expense', { p_id: expenseId, p_organization_id: context.organizationId })
      if (error) throw error
    },
  }
}
